import { useMemo, useState, useLayoutEffect } from "react";
import { Flex, Card, Empty, Spin, App, Modal } from "antd";

import { DragDropContext } from "@hello-pangea/dnd";

import KanbanColumn from "./KanbanColumn";

import { updateItem } from "@/api/tasks";

import {
  getTaskFinalStatusName,
  isTaskStatusFinal,
  STATUS_EDIT_ROLES,
} from "@/constants/tasks";
import ActualLaborCostsModal from "../modals/ActualLaborCostsModal";

/**
 * Валидирует возможность изменения статуса задачи
 * @param {Object} task - Объект задачи
 * @param {Object} newStatus - Новый статус объект

 * @returns {Object} { isValid: boolean, message: string }
 */
export const validateTaskStatusChange = (task, newStatus, draftStatus, isAdmins = false) => {
  const isUserCanEditStatus = isAdmins || task?.roles?.some((role) =>
    STATUS_EDIT_ROLES.includes(role)
  );

  // Проверка 1: Есть ли права на редактирование статуса
  if (!isUserCanEditStatus || task.readOnly) {
    return {
      isValid: false,
      message: "У вас нет прав на изменение статуса задачи",
    };
  }

  // Проверка 2: Нельзя перенести в статус Черновик
  if (newStatus?.is_for_draft) {
    return {
      isValid: false,
      message: "Невозможно переместить задачу в статус Черновик",
    };
  }

  // Проверка 3: Нельзя изменить статус завершенной или удаленной задачи
  if (isTaskStatusFinal(task?.status)) {
    const statusName = getTaskFinalStatusName(task?.status);
    return {
      isValid: false,
      message: `Невозможно изменить статус задачи со статусом "${statusName}"`,
    };
  }

  // Проверка 4: Из Черновика можно перенести только в Неначата
  if (task?.status?.is_for_draft && !newStatus?.is_default) {
    return {
      isValid: false,
      message:
        'Задача со статусом "Черновик" может быть переведена только в статус "Не начата"',
    };
  }

  // Проверка 5: Если у задачи есть родитель со статусом Черновик, запретить перемещение
  if (task?.parent_task_uuid) {
    if (task.parent?.status_uuid === draftStatus?.uuid) {
      return {
        isValid: false,
        message:
          "Родительская задача находится в статусе черновик. Ее дочерние задачи могут быть только со статусом черновик",
      };
    }
  }

  return { isValid: true, message: "" };
};

export const validateAcceptanceActualCosts = (task, newStatus) => {
  const isAcceptance = newStatus?.is_for_acceptance;

  if (!isAcceptance) {
    return { isValid: true };
  }

  if (!task?.require_actuals_on_acceptance) {
    return { isValid: true };
  }

  const hasActuals = Number(task?.actual_costs || 0) > 0;

  if (!hasActuals) {
    return {
      isValid: false,
      reason: "REQUIRE_ACTUAL_COSTS",
      message:
        "Обязательно заполнение фактических трудозатрат. Перед переводом задачи в приемку необходимо указать часы.",
    };
  }

  return { isValid: true };
};

const confirmDeleteStatus = (task, newStatus) => {
  return new Promise((resolve, reject) => {
    Modal.confirm({
      title: `Вы уверены, что хотите удалить задачу "${task.name}"?`,
      content: "Задача будет переведена в статус 'Удалена'",
      okText: "Удалить",
      cancelText: "Отмена",
      onOk: () => resolve(true),
      onCancel: () => reject(false),
    });
  });
};

// Компонент KanbanBoard для отображения доски Канбан
const KanbanBoard = ({
  data,
  loading,
  statuses = [],
  priorities = [],
  taskTypes = [],
  visibleStatuses = [],
  unit,
  isAdmins = false,
  onAddNew,
  selectable = false,
  selectedTaskUuids = [],
  onSelectTask,
}) => {
  const { message } = App.useApp();

  // Локальное состояние для оптимистичного обновления
  const [localData, setLocalData] = useState(data);

  // Синхронизируем локальные данные с пропсами до пейнта, чтобы избежать мерцания
  useLayoutEffect(() => {
    setLocalData(data);
  }, [data]);

  //   Дефолтный статус "Не начата"
  const defaultStatus = useMemo(() => {
    return statuses.find((status) => status.is_default) || null;
  }, [statuses]);

  // Статус "Черновик"
  const draftStatus = useMemo(() => {
    return statuses.find((status) => status.is_for_draft) || null;
  }, [statuses]);

  // Обязательное заполнение фактических часов
  const [showActualLaborCostsModal, setShowActualLaborCostsModal] = useState(false);
  const [taskForActualCosts, setTaskForActualCosts] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);

  // Формируем структуру доски
  const columns = useMemo(() => {
    return statuses
      .filter((status) => visibleStatuses.includes(status.uuid))
      .map((status) => {
        // Фильтруем задачи для текущего статуса
        const statusTasks = localData.filter(
          (task) => task.status?.uuid === status.uuid
        );

        return {
          ...status,

          cards: statusTasks,
        };
      });
  }, [localData, statuses, visibleStatuses]);

  // Обработчик окончания перетаскивания
  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    // Если карточка не была перемещена в другое место
    if (!destination) {
      return;
    }

    // Если карточка была переложена в тот же столбец
    if (source.droppableId === destination.droppableId) {
      if (source.index !== destination.index)
        message.warning("Нельзя ранжировать карточки вручную");
      return;
    }

    // Находим задачу
    const task = localData.find((t) => t.uuid === draggableId);
    if (!task) {
      return;
    }

    // Получаем новый статус из ID колонки
    const newStatus = statuses.find((s) => s.uuid === destination.droppableId);

    // Валидируем возможность изменения статуса
    const validation = validateTaskStatusChange(task, newStatus, draftStatus, isAdmins);

    if (!validation.isValid) {
      message.warning(validation.message);
      return;
    }

    // Если переводим в статус "Удалена"
    if (newStatus?.is_for_deletion) {
      try {
        await confirmDeleteStatus(task, newStatus);
      } catch {
        return;
      }
    }

    // Валидируем возможность перевода в Приемку
    const laborCostsValidation = validateAcceptanceActualCosts(task, newStatus);

    if (!laborCostsValidation.isValid) {
      message.warning(laborCostsValidation.message);
      setPendingStatus(newStatus);
      setTaskForActualCosts(task);
      setShowActualLaborCostsModal(true);
      return;
    }

    const oldBoard = [...localData];

    // Обновляем доску оптимистично
    setLocalData((prevData) =>
      prevData.map((t) =>
        t.uuid === task.uuid
          ? { ...t, status: newStatus, status_uuid: newStatus.uuid }
          : t
      )
    );

    try {
      // Обновляем задачу с новым статусом на сервере
      await updateItem({
        ...task,
        status_uuid: newStatus.uuid,
      });

      message.success("Статус задачи успешно обновлен");
    } catch (error) {
      message.error(error.message || "Ошибка при обновлении статуса задачи");
      // При ошибке возвращаем данные из пропсов
      setLocalData(oldBoard);
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: "400px" }}>
        <Spin size="large" />
      </Flex>
    );
  }

  if (!statuses.length) {
    return (
      <Card>
        <Empty description="Нет доступных статусов" />
      </Card>
    );
  }

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Flex
          gap="middle"
          style={{ overflowX: "auto", paddingBottom: 10, height: "100%" }}
        >
          {columns.map((status) => {
            return (
              <KanbanColumn
                key={status.uuid}
                isDefault={status.uuid === defaultStatus?.uuid}
                status={status}
                tasks={status.cards}
                statuses={statuses}
                priorities={priorities}
                taskTypes={taskTypes}
                isAdmins={isAdmins}
                unit={unit}
                onAddNew={onAddNew}
                selectable={selectable}
                selectedTaskUuids={selectedTaskUuids}
                onSelectTask={onSelectTask}
              />
            );
          })}
        </Flex>
      </DragDropContext>

      {showActualLaborCostsModal && (
        <ActualLaborCostsModal
          data={null}
          task_uuid={taskForActualCosts?.uuid}
          unit_uuid={taskForActualCosts?.unit_uuid}
          visible={showActualLaborCostsModal}
          onCancel={() => {
            setShowActualLaborCostsModal(false);
            setTaskForActualCosts(null);
          }}
          onSave={(v) => {
            const normalizedActualCosts = Number(v.actual_costs) || 0;

            setLocalData((prev) =>
              prev.map((t) =>
                t.uuid === taskForActualCosts.uuid
                  ? {
                    ...t,
                    actual_costs: normalizedActualCosts,
                    status: pendingStatus,
                    status_uuid: pendingStatus.uuid,
                  }
                  : t
              )
            );

            updateItem({
              ...taskForActualCosts,
              actual_costs: normalizedActualCosts,
              status_uuid: pendingStatus.uuid,
            });

            setPendingStatus(null);
            setTaskForActualCosts(null);
            setShowActualLaborCostsModal(false);

            message.success("Статус задачи успешно обновлен");
          }}

          isAdmins={isAdmins}
          text="Обязательно заполнение фактических трудозатрат. Перед изменением статуса задачи заполните трудозатраты"
        />
      )}

    </>
  );
};

export default KanbanBoard;
